import { stringify } from "qs";
import { emptyApi } from "./emptyApi";
import type {
  AccountsPayableData,
  AccountsReceivableData,
  AgingSummaryData,
  AnalysisData,
  AnalysisType,
  CashBalanceAnalysis,
  FinancialMetrics,
  MoMBreakdownData,
  ReportType,
} from "../models/financialMetrics";

export type {
  AccountsPayableData,
  AccountsReceivableData,
  AgingPeriod,
  AgingSummaryData,
  AnalysisData,
  AnalysisType,
  BillItem,
  CashBalanceAnalysis,
  CashBalanceMonthlyBreakdown,
  CategoryBreakdown,
  CategoryMonthlyData,
  CustomerVendorItem,
  FinancialMetrics,
  InvoiceItem,
  MoMBreakdownData,
  MoMBreakdownItem,
  ReportType,
} from "../models/financialMetrics";

export type ForecastQueryParams = {
  assumption_set_id?: string;
  forecast_start_date?: string;
  forecast_end_date?: string;
};

const financialMetricsApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getFinancialMetrics: build.query<
      FinancialMetrics,
      {
        entityId: string;
        start_date: string;
        end_date: string;
        is_custom_date_range?: boolean;
      }
    >({
      query: ({ entityId, start_date, end_date, is_custom_date_range }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/financial-metrics/${stringify(
          { start_date, end_date, is_custom_date_range },
          { skipNulls: true, addQueryPrefix: true }
        )}`,
      }),
      providesTags: ["FINANCIAL_SUMMARY"],
    }),

    getCashBalanceAnalysis: build.query<
      CashBalanceAnalysis,
      {
        entityId: string;
        start_date: string;
        end_date: string;
      } & ForecastQueryParams
    >({
      query: ({
        entityId,
        start_date,
        end_date,
        assumption_set_id,
        forecast_start_date,
        forecast_end_date,
      }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/cash-balance-analysis/${stringify(
          {
            start_date,
            end_date,
            assumption_set_id,
            forecast_start_date,
            forecast_end_date,
          },
          { skipNulls: true, addQueryPrefix: true }
        )}`,
      }),
      providesTags: ["CASH_BALANCE"],
    }),

    getEntityProfitability: build.query<
      { is_profitable: boolean },
      { entityId: string }
    >({
      query: ({ entityId }) => ({
        url: `/api/inkle/dashboards/v1/entity/${entityId}/profitability-ytd/`,
      }),
      providesTags: ["ENTITY_PROFITABILITY"],
    }),

    getAnalysis: build.query<
      AnalysisData,
      {
        entityId: string;
        start_date: string;
        end_date: string;
        report_type: ReportType;
        analysis_type: AnalysisType;
        is_custom_date_range?: boolean;
      }
    >({
      query: ({
        entityId,
        start_date,
        end_date,
        report_type,
        analysis_type,
        is_custom_date_range,
      }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/analysis/${stringify(
          {
            start_date,
            end_date,
            report_type,
            analysis_type,
            is_custom_date_range,
          },
          { skipNulls: true, addQueryPrefix: true }
        )}`,
      }),
      providesTags: ["ANALYSIS"],
    }),

    getAccountsReceivableAnalysis: build.query<
      AccountsReceivableData,
      {
        entityId: string;
        start_date: string;
        end_date: string;
      }
    >({
      query: ({ entityId, start_date, end_date }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/accounts-receivable-analysis/${stringify(
          { start_date, end_date },
          { skipNulls: true, addQueryPrefix: true }
        )}`,
      }),
      providesTags: ["AR_ANALYSIS"],
    }),

    getAccountsPayableAnalysis: build.query<
      AccountsPayableData,
      {
        entityId: string;
        start_date: string;
        end_date: string;
      }
    >({
      query: ({ entityId, start_date, end_date }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/accounts-payable-analysis/${stringify(
          { start_date, end_date },
          { skipNulls: true, addQueryPrefix: true }
        )}`,
      }),
      providesTags: ["AP_ANALYSIS"],
    }),

    getARAgingSummaryInsights: build.query<
      AgingSummaryData,
      { entityId: string; start_date: string; end_date: string }
    >({
      query: ({ entityId, start_date, end_date }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/ar-aging-summary-insights/${stringify(
          { start_date, end_date },
          { skipNulls: true, addQueryPrefix: true }
        )}`,
      }),
      providesTags: ["AR_AGING"],
    }),

    getAPAgingSummaryInsights: build.query<
      AgingSummaryData,
      { entityId: string; start_date: string; end_date: string }
    >({
      query: ({ entityId, start_date, end_date }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/ap-aging-summary-insights/${stringify(
          { start_date, end_date },
          { skipNulls: true, addQueryPrefix: true }
        )}`,
      }),
      providesTags: ["AP_AGING"],
    }),

    getMoMBreakdown: build.query<
      MoMBreakdownData,
      {
        entityId: string;
        start_date: string;
        end_date: string;
        report_type: "REVENUE" | "EXPENSE";
      } & ForecastQueryParams
    >({
      query: ({
        entityId,
        start_date,
        end_date,
        report_type,
        assumption_set_id,
        forecast_start_date,
        forecast_end_date,
      }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/mom-breakdown/${stringify(
          {
            start_date,
            end_date,
            report_type,
            assumption_set_id,
            forecast_start_date,
            forecast_end_date,
          },
          { skipNulls: true, addQueryPrefix: true }
        )}`,
      }),
      providesTags: ["MOM_BREAKDOWN"],
    }),

    resetFinancialSummaryCache: build.mutation<void, { entityId: string }>({
      query: ({ entityId }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/financial-summary-cache-reset/`,
        method: "DELETE",
      }),
      invalidatesTags: [
        "FINANCIAL_SUMMARY",
        "CASH_BALANCE",
        "ANALYSIS",
        "AR_ANALYSIS",
        "AP_ANALYSIS",
        "AR_AGING",
        "AP_AGING",
        "MOM_BREAKDOWN",
        "REVENUE_METRICS",
      ],
    }),

    exportFinancialSummary: build.mutation<
      { document_id: string },
      {
        groupId: string;
        entityId: string;
        graphs: string[];
      }
    >({
      query: ({ groupId, entityId, graphs }) => ({
        url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/financial-summary/export/`,
        method: "POST",
        body: { graphs },
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetFinancialMetricsQuery,
  useGetCashBalanceAnalysisQuery,
  useGetEntityProfitabilityQuery,
  useGetAnalysisQuery,
  useGetAccountsReceivableAnalysisQuery,
  useGetAccountsPayableAnalysisQuery,
  useGetARAgingSummaryInsightsQuery,
  useGetAPAgingSummaryInsightsQuery,
  useGetMoMBreakdownQuery,
  useResetFinancialSummaryCacheMutation,
  useExportFinancialSummaryMutation,
} = financialMetricsApi;
